import { FullScreenLoading } from '@civilgrid/ui/components';
import * as Toast from '@radix-ui/react-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { RouterProvider } from '@tanstack/react-router';
import { useEffect } from 'react';
import { MapProvider } from 'react-map-gl/mapbox';
import { useSnapshot } from 'valtio';
import authStore from './authStore';
import { shouldRetryQuery } from './common/authUtils';
import { useLayerConfigBootstrap } from './hooks/useLayerConfigBootstrap';
import { useLayerConfigSync } from './hooks/useLayerConfigSync';
import { router } from './routes/router';
import type { RouterContext } from './routes/routeTree';
import rootStore from './store';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: shouldRetryQuery,
      throwOnError: (error) => {
        if (import.meta.env.DEV) {
          console.error('Query Client Error: ', error);
        }
        return false;
      },
    },
  },
});

/**
 * Per-user layer-config wiring: the first-use cutover/bootstrap and
 * its CG-511 write half (debounced whole-document sync + lifecycle flushes). A child of
 * QueryClientProvider because the bootstrap's catalog supply runs through react-query —
 * it cannot live in App itself, which renders the provider.
 */
function LayerConfigEffects() {
  useLayerConfigBootstrap();
  useLayerConfigSync();
  return null;
}

export default function App() {
  const { isTokenLoading, tokenState, redirectUri } = useSnapshot(authStore);
  const { me } = useSnapshot(rootStore) as typeof rootStore;

  useEffect(() => {
    (async () => {
      await authStore.init();
    })();
  }, []);

  // Clear the query client when the token state is invalid to prevent stale data
  useEffect(() => {
    if (tokenState === 'INVALID') {
      queryClient.clear();
    }
  }, [tokenState]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: legacy
  useEffect(() => {
    if (!isTokenLoading) {
      void router.invalidate();
    }
  }, [isTokenLoading, tokenState, redirectUri, me]);

  if (isTokenLoading) {
    return <FullScreenLoading />;
  }

  const context: RouterContext = {
    auth: {
      tokenState,
      isTokenLoading,
      redirectUri,
    },
    me,
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LayerConfigEffects />
      <MapProvider>
        <Toast.Provider swipeDirection="right">
          <RouterProvider router={router} context={context} />
        </Toast.Provider>
      </MapProvider>
      {import.meta.env.DEV && !window.location.pathname.toLowerCase().includes('headless') && (
        <ReactQueryDevtools initialIsOpen={false} buttonPosition="bottom-right" />
      )}
    </QueryClientProvider>
  );
}
